/**
 * Year-versioned Thai tax configuration (PLAN.md "Evolving Thai tax law" risk + CLAUDE.md core
 * invariant: "EVERY tax figure ... is a NAMED constant in a year-versioned tax config with a
 * SOURCE CITATION comment. NO unexplained magic numbers").
 *
 * Thai tax law is in flux (Thai ESG -> Thai ESGX caps/holding periods, the THB 500k combined
 * retirement cap composition, the Por.161/162 remittance rule). Pinning every figure here, keyed
 * by tax year, means a future year can be added by appending one entry — never a rewrite — and the
 * golden tests in test/tax-th.test.ts lock current behaviour against the published Revenue
 * Department / Big-4 / Sherrings tables.
 *
 * TARGET YEAR. Today is 2026-06-14; the latest CONFIRMED/enacted figures are TAX YEAR 2025 (PIT
 * returns filed Jan–Mar 2026). PwC Worldwide Tax Summaries for Thailand was last reviewed
 * 2026-02-02 and is the primary cross-check; rd.go.th is the legal source.
 *
 * SOURCES (URL + as-of date), referenced by tag below:
 *  [RD-PIT]   Revenue Department, PIT rate schedule — https://www.rd.go.th/english/6045.html
 *             and the ASEAN summary PDF https://www.rd.go.th/fileadmin/user_upload/AEC/AseanTax-Thailand.pdf
 *             (PIT 5–35%). FORMULA authoritative; the page's BRACKET TABLE is a stale TY2013/14
 *             cache that wrongly merges the top at 4M — see the PIT TOP-BAND CONFLICT note below;
 *             the current 5M split is per [PWC-PIT][MAZARS]. As-of 2026-06-14.
 *  [PWC-PIT]  PwC Worldwide Tax Summaries — Thailand, "Taxes on personal income" (brackets);
 *             "Deductions" (allowances, fund caps); "Income determination" (dividends/interest/CG).
 *             https://taxsummaries.pwc.com/thailand/individual/  Last reviewed 2026-02-02.
 *  [PWC-CIT]  PwC WTS — Thailand corporate income tax, standard rate 20%.
 *             https://taxsummaries.pwc.com/thailand/corporate/taxes-on-corporate-income  2026-02-02.
 *  [SH-RMF]   Sherrings — personal tax deductions/allowances, RMF/PVD/combined cap.
 *             https://sherrings.com/personal-tax-deductions-allowances-thailand.html  2026-06-14.
 *  [SH-SSF]   Sherrings — SSF allowances (30%, THB 200k, 10y hold, 1.5%/mo surcharge on early exit).
 *             https://sherrings.com/ssf-investment-tax-allowances-thailand.html  2026-06-14.
 *  [SH-ESG]   Sherrings — Thai ESG / Thai ESGX incentives (caps + holding periods + measure dates).
 *             https://sherrings.com/esg-mutual-fund-tax-incentive-thailand.html  2026-06-14.
 *  [MAZARS]   Forvis Mazars TH — personal income tax (50% / THB 100k expense deduction).
 *             https://www.forvismazars.com/th/en/insights/doing-business-in-thailand/payroll/personal-income-tax  2026-06-14.
 *  [POR162]   RD Orders Por.161/2566 & Por.162/2566 (foreign-sourced income remitted; effective
 *             2024-01-01; resident >=180 days). Summaries: Forvis Mazars / HLB Thailand / MPG.
 *             https://www.forvismazars.com/th/en/insights/doing-business-in-thailand/tax/revenue-department-s-guidance-on-foreign-income  2026-06-14.
 *  [RD-47bis] Revenue Code s.47 bis (dividend tax credit / imputation gross-up).
 *             https://www.rd.go.th/english/37749.html  2026-06-14.
 *
 * CONFLICTS / RECENTLY-CHANGED (called out per the brief — do NOT silently pick one):
 *  - PIT TOP-BAND SOURCE CONFLICT (most important): the RD English page [RD-PIT] (rd.go.th/
 *    english/6045.html) currently RENDERS A STALE (TY2013/14-era) cached bracket table that MERGES
 *    the top into "2,000,001–4,000,000 -> 30%, over 4,000,000 -> 35%". The CURRENT ENACTED law
 *    (since the 2017 top-band split) is "2,000,001–5,000,000 -> 30%" and "over 5,000,000 -> 35%",
 *    confirmed by PwC, Statrys and Forvis Mazars [PWC-PIT][MAZARS] and required by PLAN.md ("top 35%
 *    over 5,000,000"). We ENCODE THE 5M SPLIT (pitBrackets below) and cite the stale RD page ONLY for
 *    the assessable-income FORMULA, never for the top edge. Do NOT "correct" the config back to 4M.
 *  - Thai ESG vs Thai ESGX: the ONGOING Thai ESG cap stepped from THB 100k (8-year hold, the
 *    2023 launch terms [SH-ESG]) to THB 300k (5-year hold) for purchases in tax years 2024–2026
 *    [SH-ESG][PWC-PIT]. Thai ESGX is a SEPARATE 2025-only measure (purchases May–Jun 2025): new
 *    money up to THB 300k / 5-year hold, PLUS an LTF-switch tranche up to THB 500k spread over
 *    2025–2029 [SH-ESG]. We model the 2025 ESG/ESGX cap at THB 300k (the new-money figure both
 *    funds share); the LTF-switch tranche and the post-2026 reversion to THB 100k/8y are TODOs.
 *  - The THB 500,000 COMBINED cap composition: RMF + SSF + PVD + GPF (govt pension) + NSF
 *    (national savings) + registered-provident/teacher funds + qualified pension life insurance
 *    [SH-RMF][SH-SSF]. Thai ESG / ESGX are NOT inside this THB 500k umbrella — they have their own
 *    separate caps [SH-ESG][PWC-PIT].
 *  - Por.161/162 remittance: enacted (effective 2024-01-01) but interpretation is still evolving;
 *    pre-2024 foreign income remitted is protected [POR162]. The worldwide-income (taxed-when-
 *    earned) proposal is NOT yet law and is OUT OF SCOPE (TODO in index.ts).
 */

/** A single progressive PIT bracket: marginal `rate` (decimal) applies to net income in
 *  (`lower`, `upper`]. The top bracket uses `upper: Infinity`. */
export interface PitBracket {
  /** Exclusive lower edge of the band, in THB. */
  readonly lower: number;
  /** Inclusive upper edge of the band, in THB (Infinity for the top band). */
  readonly upper: number;
  /** Marginal rate as a decimal (e.g. 0.2 = 20%). */
  readonly rate: number;
}

/** Deduction terms for one tax-advantaged investment vehicle. */
export interface FundDeductionRule {
  /** Maximum deduction as a fraction of assessable income (e.g. 0.30). */
  readonly pctOfIncome: number;
  /** Standalone cap in THB for this vehicle's own deduction. */
  readonly cap: number;
  /** Minimum holding period in years; selling earlier triggers clawback. */
  readonly holdingYears: number;
  /** Whether this vehicle counts toward the THB 500k COMBINED retirement cap. */
  readonly inCombinedCap: boolean;
}

/** Everything the ThailandTaxPolicy needs for one tax year. */
export interface ThaiTaxYearConfig {
  /** The tax year these figures are confirmed for. */
  readonly taxYear: number;

  // --- PIT schedule + allowances [RD-PIT][PWC-PIT] -----------------------------------------
  /** Progressive PIT brackets (ascending). [RD-PIT][PWC-PIT] */
  readonly pitBrackets: readonly PitBracket[];
  /** Personal allowance, THB. [PWC-PIT][SH-RMF] */
  readonly personalAllowance: number;
  /** Spouse allowance (if the spouse does not file separately), THB. [PWC-PIT] */
  readonly spouseAllowance: number;
  /** Child allowance per child, THB. [PWC-PIT] */
  readonly childAllowance: number;
  /** Employment-income expense deduction: `min(income * pct, cap)`. [MAZARS][PWC-PIT] */
  readonly employmentExpenseDeductionPct: number;
  readonly employmentExpenseDeductionCap: number;

  // --- Investment-income withholding [PWC-PIT] ---------------------------------------------
  /** Dividend withholding-tax rate (final-tax election). [PWC-PIT] */
  readonly dividendWhtRate: number;
  /** Interest withholding-tax rate (final-tax election). [PWC-PIT] */
  readonly interestWhtRate: number;
  /** Standard corporate income-tax rate — the gross-up rate for the s.47 bis dividend tax
   *  credit / imputation election. [PWC-CIT][RD-47bis] */
  readonly corporateTaxRate: number;

  // --- Tax-advantaged vehicles [SH-RMF][SH-SSF][SH-ESG] ------------------------------------
  /** RMF: 30% of income, THB 500k, hold to age 55 AND >= 5y; counts to combined cap. */
  readonly rmf: FundDeductionRule;
  /** SSF: 30% of income, THB 200k, 10y hold; counts to combined cap. */
  readonly ssf: FundDeductionRule;
  /** Thai ESG (and the 2025 ESGX new-money tranche): 30%, THB 300k, 5y hold; SEPARATE cap. */
  readonly thaiEsg: FundDeductionRule;
  /** PVD (provident fund employee side): 15% of income, THB 500k; counts to combined cap. */
  readonly pvd: FundDeductionRule;
  /** The THB 500k cap shared across all vehicles whose rule has `inCombinedCap: true`. */
  readonly combinedRetirementCap: number;

  // --- Early-withdrawal surcharge [SH-SSF] -------------------------------------------------
  /** Monthly surcharge on tax recovered when a fund is sold before its holding period
   *  (1.5%/month per RD; we model an annualised single-shot surcharge — see index.ts clawback). */
  readonly earlyWithdrawalSurchargeMonthly: number;
}

/** RMF age-55 withdrawal-eligibility rule (PLAN.md §2a). Shared with strategies/. */
export const RMF_ELIGIBILITY_AGE = 55;

/**
 * TAX YEAR 2025 — the latest confirmed Thai figures. Every number carries its source tag (see the
 * file header). Adding 2027 later = append a `TH_TAX_YEAR_2027` and register it in `THAI_TAX_YEARS`.
 */
export const TH_TAX_YEAR_2025: ThaiTaxYearConfig = {
  taxYear: 2025,

  // PIT brackets: 0% <=150k; 5/10/15/20/25/30%; 30% to 5M then top 35% OVER 5,000,000.
  // Top edge is the CURRENT enacted 5M split [PWC-PIT][MAZARS], NOT the stale RD-page 4M table
  // (see PIT TOP-BAND CONFLICT in the header). RD page [RD-PIT] cited for the formula only.
  pitBrackets: [
    { lower: 0, upper: 150_000, rate: 0.0 },
    { lower: 150_000, upper: 300_000, rate: 0.05 },
    { lower: 300_000, upper: 500_000, rate: 0.1 },
    { lower: 500_000, upper: 750_000, rate: 0.15 },
    { lower: 750_000, upper: 1_000_000, rate: 0.2 },
    { lower: 1_000_000, upper: 2_000_000, rate: 0.25 },
    { lower: 2_000_000, upper: 5_000_000, rate: 0.3 },
    { lower: 5_000_000, upper: Number.POSITIVE_INFINITY, rate: 0.35 },
  ],

  personalAllowance: 60_000, // [PWC-PIT][SH-RMF]
  spouseAllowance: 60_000, // [PWC-PIT] (spouse not filing separately)
  childAllowance: 30_000, // [PWC-PIT] (+THB 30k for 2nd child onward born >=2018 — TODO refine)
  employmentExpenseDeductionPct: 0.5, // [MAZARS][PWC-PIT]
  employmentExpenseDeductionCap: 100_000, // [MAZARS][PWC-PIT]

  dividendWhtRate: 0.1, // [PWC-PIT] 10% final-tax election
  interestWhtRate: 0.15, // [PWC-PIT] 15% final-tax election
  corporateTaxRate: 0.2, // [PWC-CIT] standard CIT — the s.47 bis gross-up rate [RD-47bis]

  rmf: { pctOfIncome: 0.3, cap: 500_000, holdingYears: 5, inCombinedCap: true }, // [SH-RMF]
  ssf: { pctOfIncome: 0.3, cap: 200_000, holdingYears: 10, inCombinedCap: true }, // [SH-SSF]
  thaiEsg: { pctOfIncome: 0.3, cap: 300_000, holdingYears: 5, inCombinedCap: false }, // [SH-ESG]
  pvd: { pctOfIncome: 0.15, cap: 500_000, holdingYears: 5, inCombinedCap: true }, // [SH-RMF]
  combinedRetirementCap: 500_000, // [SH-RMF][SH-SSF]

  earlyWithdrawalSurchargeMonthly: 0.015, // [SH-SSF] 1.5%/month
};

/** Registry of confirmed tax years. Append future years here; never mutate a past entry. */
export const THAI_TAX_YEARS: Readonly<Record<number, ThaiTaxYearConfig>> = {
  2025: TH_TAX_YEAR_2025,
};

/** The latest confirmed tax year — the default the policy uses for any year >= it. */
export const LATEST_THAI_TAX_YEAR = 2025;

/**
 * Resolve the config for a calendar `year`. Years before the earliest confirmed year, or after the
 * latest, fall back to the NEAREST confirmed year (clamped) — Thai law is pinned to enacted years,
 * so projecting forward reuses the latest confirmed schedule rather than inventing figures. Exact
 * matches return that year's config.
 */
export function resolveThaiTaxConfig(year: number): ThaiTaxYearConfig {
  const exact = THAI_TAX_YEARS[year];
  if (exact !== undefined) {
    return exact;
  }
  const years = Object.keys(THAI_TAX_YEARS)
    .map(Number)
    .sort((a, b) => a - b);
  const earliest = years[0] as number;
  const latest = years[years.length - 1] as number;
  const clamped = year < earliest ? earliest : latest;
  return THAI_TAX_YEARS[clamped] as ThaiTaxYearConfig;
}
